/**
 * Spec 220 PR1 — SignatureCanvas (Path A: in-browser canvas signature).
 *
 * Modal that captures a client signature using `PointerEvent` (one
 * codepath for mouse, touch and pen) and emits an SVG path string in
 * the same shape mobile produces (`mobile/src/components/SignatureModal.tsx`).
 * That string is what `work.signature` stores so the rendered output is
 * pixel-equivalent across surfaces — see spec 220 §3.1 for the format
 * decision.
 *
 * Self-contained: this component does NOT touch the work store, sync,
 * or encryption. It only renders the canvas and returns the path
 * string to its parent through `onSave`. The parent is responsible
 * for `saveWork` + `syncWork` + the corresponding `signedAt` bump.
 *
 * Coordinates are captured relative to the SVG's bounding rect
 * (`getBoundingClientRect`) so the path coordinates match the
 * `viewBox` exactly regardless of CSS scaling. `touch-action: none`
 * on the SVG suppresses the browser's native pan/zoom on touch
 * devices, which would otherwise eat half of the pointer move events.
 *
 * The viewBox is `0 0 600 240` per spec 220 §3.1.3. The existing
 * render in `web/src/routes/WorkDetail.tsx` uses `0 0 300 120` (the
 * mobile capture size); the integrating PR will widen it so the
 * signature scales to the same logical box. Until then, paths
 * produced here render at half-size on the existing surface — that
 * is acceptable because PR1 ships the component only.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import './SignatureCanvas.css';

export interface SignatureCanvasProps {
  /** Whether the modal is visible. When false, the component renders nothing. */
  open: boolean;
  /** Called when the user dismisses the modal without saving (Cancel / Escape / backdrop). */
  onClose: () => void;
  /**
   * Called with the captured SVG path string when the user confirms.
   * Format mirrors mobile: each stroke is `M x,y L x,y L x,y …` and the
   * full payload is the strokes joined by spaces. Never invoked with an
   * empty path — Confirmar stays disabled until at least one stroke
   * lands on the canvas.
   */
  onSave: (pathSvg: string) => void;
}

/** Capture-area logical size (matches viewBox). Coordinates are stored at this resolution. */
const VIEWBOX_W = 600;
const VIEWBOX_H = 240;

interface Point {
  x: number;
  y: number;
}

export function SignatureCanvas({ open, onClose, onSave }: SignatureCanvasProps) {
  // Each stroke is a complete SVG path string ("M x,y L x,y L x,y …"),
  // matching the mobile output. We append `L` segments to the last
  // stroke as the pointer moves, then start a fresh stroke on the
  // next pointerdown.
  const [strokes, setStrokes] = useState<string[]>([]);
  const svgRef = useRef<SVGSVGElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const currentPoints = useRef<Point[]>([]);
  const drawingPointerId = useRef<number | null>(null);

  // Reset the canvas every time the modal opens so a previous,
  // dismissed sketch doesn't leak into the next session.
  useEffect(() => {
    if (open) {
      setStrokes([]);
      currentPoints.current = [];
      drawingPointerId.current = null;
    }
  }, [open]);

  // WAI-ARIA APG modal-dialog focus management — same pattern as
  // PhotoViewer. Restore focus to the trigger on close.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = (document.activeElement as HTMLElement | null) ?? null;
    dialogRef.current?.focus();
    return () => {
      previouslyFocused.current?.focus();
    };
  }, [open]);

  // Escape closes without saving. We listen at document level so the
  // shortcut works regardless of which control inside the dialog has
  // focus.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /**
   * Convert a pointer event to viewBox coordinates. `clientX/Y` minus
   * the SVG's bounding rect, then scaled from the rendered (CSS) size
   * to the logical viewBox so coordinates always live in the
   * 0..VIEWBOX_W / 0..VIEWBOX_H space — independent of the actual
   * rendered width on the user's screen.
   */
  const pointFromEvent = useCallback((e: React.PointerEvent<SVGSVGElement>): Point => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    // Avoid divide-by-zero when the element is detached / hidden.
    const scaleX = rect.width > 0 ? VIEWBOX_W / rect.width : 1;
    const scaleY = rect.height > 0 ? VIEWBOX_H / rect.height : 1;
    return {
      x: Math.round((e.clientX - rect.left) * scaleX),
      y: Math.round((e.clientY - rect.top) * scaleY),
    };
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      // preventDefault stops the browser's default text-selection /
      // pan-zoom behaviour on touchscreens; without this Chrome on
      // Android can swallow the move events as a scroll gesture.
      e.preventDefault();
      // Capture the pointer on the SVG so subsequent move/up events
      // keep firing on this element even if the cursor leaves the
      // bounding rect mid-stroke.
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Some test environments (happy-dom) don't implement pointer
        // capture; the events still fire, just without capture
        // semantics.
      }
      const { x, y } = pointFromEvent(e);
      currentPoints.current = [{ x, y }];
      drawingPointerId.current = e.pointerId;
      setStrokes((prev) => [...prev, `M ${x},${y}`]);
    },
    [pointFromEvent],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (drawingPointerId.current !== e.pointerId) return;
      e.preventDefault();
      const { x, y } = pointFromEvent(e);
      const next = [...currentPoints.current, { x, y }];
      currentPoints.current = next;
      const [p0, ...rest] = next;
      const d = `M ${p0.x},${p0.y} ` + rest.map((p) => `L ${p.x},${p.y}`).join(' ');
      // Replace the in-progress stroke (last entry) with the updated
      // path string. Same pattern as mobile.
      setStrokes((prev) => [...prev.slice(0, -1), d]);
    },
    [pointFromEvent],
  );

  const handlePointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (drawingPointerId.current !== e.pointerId) return;
    drawingPointerId.current = null;
    currentPoints.current = [];
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // See setPointerCapture comment above.
    }
  }, []);

  function clear() {
    setStrokes([]);
    currentPoints.current = [];
    drawingPointerId.current = null;
  }

  function confirm() {
    if (strokes.length === 0) return;
    const pathSvg = strokes.join(' ');
    onSave(pathSvg);
    onClose();
  }

  if (!open) return null;
  const hasSignature = strokes.length > 0;

  return (
    <div
      ref={dialogRef}
      className="sig-backdrop"
      data-testid="signature-canvas"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sig-title"
      tabIndex={-1}
      onClick={(e) => {
        // Backdrop click cancels — same convention as PhotoViewer /
        // CommandPalette. Inner panel clicks bubble through.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sig-panel">
        <header className="sig-header">
          <h2 id="sig-title" className="sig-title">
            Firma del cliente
          </h2>
          <p className="sig-subtitle">Firme con el ratón o el dedo en el recuadro de abajo.</p>
        </header>

        <div className="sig-canvas-frame">
          <svg
            ref={svgRef}
            className="sig-canvas"
            data-testid="signature-canvas-svg"
            viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Área de firma"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            {strokes.map((d, i) => (
              <path
                key={i}
                d={d}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}
          </svg>
          {!hasSignature && (
            <span className="sig-placeholder" aria-hidden="true">
              Firme aquí
            </span>
          )}
        </div>

        <footer className="sig-actions">
          <button
            type="button"
            className="sig-btn sig-btn--ghost"
            onClick={clear}
            disabled={!hasSignature}
            data-testid="signature-canvas-clear"
          >
            Limpiar
          </button>
          <div className="sig-actions-spacer" />
          <button
            type="button"
            className="sig-btn sig-btn--secondary"
            onClick={onClose}
            data-testid="signature-canvas-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            className="sig-btn sig-btn--primary"
            onClick={confirm}
            disabled={!hasSignature}
            data-testid="signature-canvas-confirm"
          >
            Confirmar firma
          </button>
        </footer>
      </div>
    </div>
  );
}
